/**
 * HighlighterDirective class is responsible for managing text highlighting functionality.
 * It integrates with AngularJS services and jQuery plugins to provide text highlighting capabilities.
 */
class HighlighterDirective {
    /**
     * Constructor for HighlighterDirective.
     * @param {Object} $rootScope - AngularJS root scope.
     * @param {Object} $timeout - AngularJS timeout service.
     * @param {Object} TextHighlighterService - Service for managing text highlights.
     * @param {Object} $ - jQuery object.
     */
    constructor($rootScope, $timeout, TextHighlighterService, $) {
        this.$rootScope = $rootScope;
        this.$timeout = $timeout;
        this.TextHighlighterService = TextHighlighterService;
        this.$ = $;
    }

    /**
     * Link function to initialize and manage the directive's behavior.
     * @param {Object} scope - AngularJS scope.
     * @param {Object} element - DOM element where the directive is applied.
     * @param {Object} attrs - Attributes of the directive.
     */
    link(scope, element, attrs) {
        if (this.$rootScope.isPreview || !this.$rootScope.isCMS) {
            /**
             * Function to check if the highlight mode is active.
             * @returns {boolean} - True if highlight mode is active, otherwise false.
             */
            scope.isHighlightMode = () => {
                return this.$rootScope.isHighlightMode;
            };

            scope.h_uuid = attrs.highlighter;
            let isInit = false;

            /**
             * Initializes the text highlighter and sets up event listeners.
             */
            const init = () => {
                this.$(element).textHighlighter({
                    onBeforeHighlight: (highlight) => {
                        if (this.$rootScope.isHighlightMode)
                            return true;
                        return false;
                    }
                });

                const highlighter = this.$(element).data('textHighlighter');

                this.$(element).on('click', '.highlighted', (event) => {
                    highlighter.removeHighlights(event.currentTarget);
                });

                this.$(element).on('mouseup', '.highlighted', (event) => {
                    highlighter.removeHighlights(event.currentTarget);
                });

                element.on('$destroy', () => {
                    const highlights = angular.copy(highlighter.serializeHighlights());
                    this.TextHighlighterService.saveHighlight(scope.h_uuid, highlights);
                });

                if (!isInit) {
                    this.$timeout(() => {
                        const highlights = this.TextHighlighterService.getHighlights();
                        if (highlights.hasOwnProperty(scope.h_uuid)) {
                            isInit = true;
                            highlighter.deserializeHighlights(highlights[scope.h_uuid]);
                        }
                    });
                }
            };

            init();

            this.$timeout(() => {
                if (!scope.isHighlightMode() && this.$(element).data('textHighlighter')) {
                    this.$(element).data('textHighlighter').destroy();
                    this.$(element).off('click', '.highlighted');
                    this.$(element).off('mouseup', '.highlighted');
                }

                scope.$watch('isHighlightMode()', (newVal, oldVal) => {
                    if (newVal === oldVal) return false;
                    if (scope.isHighlightMode()) {
                        init();
                    } else {
                        if (this.$(element).data('textHighlighter')) {
                            this.$(element).data('textHighlighter').destroy();
                            this.$(element).off('click', '.highlighted');
                            this.$(element).off('mouseup', '.highlighted');
                        }
                    }
                });
            }, 500);
        }
    }
}

export default function registerHighlighterDirective(module) {
    module.directive('highlighter', ['$rootScope', '$timeout', 'TextHighlighterService', '$',
        ($rootScope, $timeout, TextHighlighterService, $) => {
            const directive = new HighlighterDirective($rootScope, $timeout, TextHighlighterService, $);
            return {
                restrict: 'A',
                link: (scope, element, attrs) => directive.link(scope, element, attrs)
            };
        }
    ]);
}